import toast from 'react-hot-toast';

/**
 * Utilidad para generar e imprimir el informe profesional de resultados del Índice de Propiedades.
 * Diseñado con formato horizontal (landscape), membrete institucional, resumen de parámetros
 * y tabla de intervinientes apta para impresión física y guardado en PDF de alta calidad.
 */

export const handleImprimirIndice = ({
  registros = [],
  criterios = {},
  usuario = null,
  esBúsquedaCompleta = false,
  paginaActual = 1,
  totalPaginas = 1,
}) => {
  if (!registros || registros.length === 0) {
    toast.error('No hay registros para imprimir.');
    return;
  }

  const printWindow = window.open('', '_blank', 'width=1100,height=850');
  if (!printWindow) {
    toast.error('El navegador bloqueó la ventana emergente de impresión. Por favor habilite ventanas emergentes.');
    return;
  }

  const fechaEmision = new Date().toLocaleString('es-CL', {
    dateStyle: 'full',
    timeStyle: 'short',
  });

  const fechaCorta = new Date().toLocaleDateString('es-CL');
  const horaCorta = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

  // Formatear parámetros de búsqueda para el resumen
  const esEmpresa = criterios.tipoPersona === 'empresa';
  let terminoBuscado = '';
  if (esEmpresa) {
    terminoBuscado = criterios.filtros?.apellido_paterno || 'No especificado';
  } else {
    const partes = [
      criterios.filtros?.primer_nombre,
      criterios.filtros?.apellido_paterno,
      criterios.filtros?.apellido_materno,
    ].filter(Boolean);
    terminoBuscado = partes.length > 0 ? partes.join(' ') : 'No especificado';
  }

  const tipoRegistroMap = {
    propiedad: 'Propiedad',
    hipotecas: 'Hipotecas y Gravámenes',
    prohibiciones: 'Interdicciones y Prohibiciones',
    aguas: 'Propiedad de Aguas',
    hipotecas_aguas: 'Hipotecas de Aguas',
    prohibiciones_aguas: 'Prohibiciones de Aguas',
  };

  const tipoRegistroTexto = criterios.filtros?.tipo_registro
    ? tipoRegistroMap[criterios.filtros.tipo_registro] || criterios.filtros.tipo_registro
    : 'Todos los Registros';

  const naturalezaTexto = criterios.naturalezaDescripcion || 'Todas las Naturalezas';
  const refinamientoTexto = criterios.filtroRefinar ? `"${criterios.filtroRefinar}"` : 'Ninguno';

  const filasHtml = registros
    .map((reg, index) => {
      const intervinientesHtml =
        reg.intervinientes && reg.intervinientes.length > 0
          ? reg.intervinientes
              .map((inv) => {
                const nombre = [inv.nombres, inv.apellido_paterno, inv.apellido_materno]
                  .filter(Boolean)
                  .join(' ');
                const rol = inv.rol ? `<span class="badge-rol">(${inv.rol})</span>` : '';
                return `<div class="inv-item"><strong>${nombre || '—'}</strong> ${rol}</div>`;
              })
              .join('')
          : '<span class="text-muted">—</span>';

      const naturalezaDesc =
        reg.naturaleza?.descripcion || reg.naturaleza_acto || '—';

      const direccion = reg.inmueble?.calle || '—';
      const comuna = reg.inmueble?.comuna || '—';

      return `
        <tr>
          <td class="text-center text-muted" style="font-size: 11px; font-weight: 600;">${index + 1}</td>
          <td class="col-intervinientes">${intervinientesHtml}</td>
          <td class="col-registro">
            <span class="badge-registro">${reg.tipo_registro || '—'}</span>
          </td>
          <td class="col-naturaleza">${naturalezaDesc}</td>
          <td class="text-center font-mono font-bold">${reg.foja_inicio ?? '—'}</td>
          <td class="text-center font-mono font-bold">${reg.numero ?? '—'}</td>
          <td class="text-center font-mono font-bold">${reg.anio ?? '—'}</td>
          <td class="col-inmueble">${direccion}</td>
          <td class="col-comuna">${comuna}</td>
        </tr>
      `;
    })
    .join('');

  const html = `
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="utf-8">
        <title>Informe de Búsqueda Registral — Índice de Propiedades</title>
        <style>
          @page {
            size: letter landscape;
            margin: 10mm 12mm 12mm 12mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            margin: 0;
            padding: 15px;
            background: #ffffff;
            line-height: 1.35;
            font-size: 11.5px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .no-print {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f1f5f9;
            border: 1px solid #cbd5e1;
            padding: 10px 18px;
            border-radius: 8px;
            margin-bottom: 20px;
          }

          @media print {
            .no-print {
              display: none !important;
            }
            body {
              padding: 0 !important;
            }
            tr {
              page-break-inside: avoid !important;
            }
            thead {
              display: table-header-group;
            }
          }

          /* Header Institucional */
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #003366;
            padding-bottom: 12px;
            margin-bottom: 14px;
          }

          .institution-brand {
            display: flex;
            flex-direction: column;
          }

          .institution-title {
            font-size: 17px;
            font-weight: 900;
            color: #003366;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            margin: 0;
          }

          .institution-subtitle {
            font-size: 11px;
            font-weight: 600;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-top: 2px;
          }

          .doc-report-title {
            text-align: right;
          }

          .doc-report-title h2 {
            margin: 0;
            font-size: 15px;
            font-weight: 800;
            color: #1e293b;
            text-transform: uppercase;
          }

          .doc-report-title .meta-date {
            font-size: 11px;
            color: #64748b;
            margin-top: 3px;
          }

          /* Resumen de Criterios de Búsqueda */
          .criteria-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 10px 14px;
            margin-bottom: 14px;
            display: grid;
            grid-template-columns: 2fr 1.6fr 1.4fr;
            gap: 12px;
            font-size: 11px;
          }

          .criteria-item {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .criteria-label {
            font-size: 9.5px;
            text-transform: uppercase;
            font-weight: 700;
            color: #64748b;
            letter-spacing: 0.3px;
          }

          .criteria-val {
            font-size: 12px;
            font-weight: 700;
            color: #003366;
          }

          .meta-pill {
            display: inline-block;
            background: #eff6ff;
            color: #1d4ed8;
            font-weight: 600;
            padding: 2px 7px;
            border-radius: 4px;
            border: 1px solid #bfdbfe;
            font-size: 10.5px;
          }

          /* Tabla de Resultados */
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          thead th {
            background: #003366 !important;
            color: #ffffff !important;
            font-weight: 700;
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            padding: 7px 8px;
            border: 1px solid #002244;
            text-align: left;
          }

          tbody td {
            padding: 6px 8px;
            border-bottom: 1px solid #e2e8f0;
            border-right: 1px solid #f1f5f9;
            border-left: 1px solid #f1f5f9;
            vertical-align: middle;
            color: #1e293b;
          }

          tbody tr:nth-child(even) {
            background-color: #f8fafc !important;
          }

          .col-intervinientes {
            min-width: 220px;
            max-width: 280px;
          }

          .inv-item {
            margin-bottom: 2px;
            line-height: 1.25;
          }

          .badge-rol {
            font-size: 9.5px;
            color: #64748b;
            font-weight: 500;
          }

          .badge-registro {
            text-transform: capitalize;
            font-weight: 600;
            color: #334155;
            font-size: 10.5px;
          }

          .col-naturaleza {
            font-size: 10.5px;
            font-weight: 600;
            color: #003366;
            max-width: 150px;
          }

          .col-inmueble {
            font-size: 10.5px;
            max-width: 170px;
          }

          .col-comuna {
            font-size: 10.5px;
            color: #475569;
          }

          .font-mono {
            font-family: 'SF Mono', Consolas, Menlo, Monaco, monospace;
          }

          .font-bold {
            font-weight: 700;
          }

          .text-center {
            text-align: center;
          }

          .text-muted {
            color: #94a3b8;
          }

          /* Footer Oficial */
          .footer-section {
            margin-top: 18px;
            padding-top: 10px;
            border-top: 1px solid #cbd5e1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 10px;
            color: #64748b;
          }

          .legal-note {
            max-width: 65%;
            line-height: 1.3;
          }

          .emisor-box {
            text-align: right;
            font-weight: 600;
            color: #334155;
          }
        </style>
      </head>
      <body>

        <!-- Barra superior para navegador (oculta al imprimir) -->
        <div class="no-print">
          <div>
            <strong style="color: #003366; font-size: 13px;">Vista Previa de Impresión — Índice de Propiedades</strong>
            <span style="color: #64748b; font-size: 12px; margin-left: 8px;">(${registros.length} registros listados)</span>
          </div>
          <div style="display: flex; gap: 10px;">
            <button 
              onclick="window.close()" 
              style="padding: 7px 16px; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 6px; cursor: pointer; font-weight: 600;"
            >
              Cerrar
            </button>
            <button 
              onclick="window.print()" 
              style="padding: 7px 20px; border: none; background: #003366; color: #ffffff; border-radius: 6px; cursor: pointer; font-weight: 700; box-shadow: 0 2px 8px rgba(0,51,102,0.3);"
            >
              🖨️ Mandar a Imprimir / Guardar PDF
            </button>
          </div>
        </div>

        <!-- Encabezado Institucional -->
        <div class="header">
          <div class="institution-brand">
            <h1 class="institution-title">Conservador de Bienes Raíces</h1>
            <span class="institution-subtitle">Sistema Integrado de Gestión Registral e Índices Históricos</span>
          </div>
          <div class="doc-report-title">
            <h2>Informe de Búsqueda de Índices</h2>
            <div class="meta-date">
              Fecha de emisión: <strong>${fechaCorta} ${horaCorta} hrs</strong>
            </div>
          </div>
        </div>

        <!-- Ficha de Criterios Aplicados -->
        <div class="criteria-card">
          <div class="criteria-item">
            <span class="criteria-label">Criterio / Titular Consultado (${esEmpresa ? 'Empresa' : 'Persona Natural'}):</span>
            <span class="criteria-val">${terminoBuscado}</span>
            <span style="font-size: 10px; color: #64748b; margin-top: 2px;">
              Alcance: <strong>${esBúsquedaCompleta ? 'Búsqueda Completa Consolidada' : `Página ${paginaActual} de ${totalPaginas}`}</strong>
            </span>
          </div>

          <div class="criteria-item">
            <span class="criteria-label">Clasificación Registral:</span>
            <span><strong>Registro:</strong> ${tipoRegistroTexto}</span>
            <span><strong>Naturaleza:</strong> ${naturalezaTexto}</span>
          </div>

          <div class="criteria-item" style="text-align: right; align-items: flex-end;">
            <span class="criteria-label">Total Coincidencias en Sistema:</span>
            <span class="meta-pill">
              ${criterios.totalInscripciones ?? registros.length} inscripciones encontradas
            </span>
            ${
              refinamientoTexto !== 'Ninguno'
                ? `<span style="font-size: 10px; color: #64748b; margin-top: 2px;">Filtro adicional: <strong>${refinamientoTexto}</strong></span>`
                : ''
            }
          </div>
        </div>

        <!-- Tabla de Registros -->
        <table>
          <thead>
            <tr>
              <th style="width: 25px; text-align: center;">#</th>
              <th>Intervinientes y Calidad</th>
              <th style="width: 105px;">Registro</th>
              <th style="width: 130px;">Naturaleza del Acto</th>
              <th style="width: 48px; text-align: center;">Foja</th>
              <th style="width: 48px; text-align: center;">Núm.</th>
              <th style="width: 45px; text-align: center;">Año</th>
              <th>Inmueble / Ubicación</th>
              <th style="width: 95px;">Comuna</th>
            </tr>
          </thead>
          <tbody>
            ${filasHtml}
          </tbody>
        </table>

        <!-- Pie de Página y Validez Legal -->
        <div class="footer-section">
          <div class="legal-note">
            <strong>AVISO LEGAL E INFORMATIVO:</strong> El presente reporte corresponde a una transcripción informativa de los registros del Índice de Propiedades. Para la acreditación fehaciente de dominio vigente, gravámenes o prohibiciones, el interesado debe requerir el Certificado oficial suscrito por el Conservador.
          </div>
          <div class="emisor-box">
            <div>Emitido por: <strong>${usuario?.name || 'Funcionario Autorizado'}</strong></div>
            <div style="font-size: 9.5px; color: #64748b;">
              ${usuario?.cargo || (usuario?.role ? `Rol: ${usuario.role.toUpperCase()}` : 'Personal CBR')} · CBR Digital
            </div>
          </div>
        </div>

        <script>
          // Ejecutar diálogo nativo de impresión automáticamente
          window.addEventListener('load', () => {
            setTimeout(() => {
              window.print();
            }, 300);
          });
        </script>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
};
